import { Link } from 'react-router'
import { LuArrowRight, LuCheck } from 'react-icons/lu'
import { cn } from '@/lib/cn'
import { Reveal } from '@/components/ui/Reveal'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { primaryLink } from '@/components/ui/linkStyles'
import { featured, secondary } from '@/data/services'

export const ServicesFeatured = () => (
  <section className="bg-base-50 py-16 sm:py-20 lg:py-24">
    <div className="mx-auto max-w-6xl px-6">
      <SectionHeader
        title="Find your"
        accent="starting point"
        description="Personal support, deeper learning, and a closer connection to your wellbeing."
      />

      <div className="mb-12 grid gap-6 lg:grid-cols-2">
        {featured.map((svc, index) => (
          <Reveal key={svc.n} delay={index * 100} className="h-full">
            <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-base-200 bg-white transition-colors hover:border-green-300">
              <div className="h-1 w-full bg-green-700" />
              <div className="flex flex-1 flex-col p-7 lg:p-9">
                <div className="mb-6 flex items-center justify-between">
                  <span className="font-display text-sm text-gold-500">{svc.n}</span>
                  <span
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium tracking-wide',
                      svc.tagColor,
                    )}
                  >
                    {svc.tag}
                  </span>
                </div>

                <h2 className="mb-4 font-display text-2xl leading-tight tracking-tight text-base-900 lg:text-[1.9rem]">
                  {svc.title}
                </h2>
                <p className="mb-7 text-base leading-7 text-base-600">{svc.summary}</p>

                <div className="mb-7 flex-1">
                  <p className="mb-3 text-xs font-medium uppercase tracking-[0.18em] text-green-700">
                    Areas of support
                  </p>
                  <ul className="space-y-2.5">
                    {svc.supports.map((s) => (
                      <li key={s} className="flex items-center gap-3 text-sm text-base-700">
                        <span
                          aria-hidden="true"
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700"
                        >
                          <LuCheck className="h-3 w-3" />
                        </span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="mb-7 flex items-center gap-2.5 text-sm text-base-600">
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-green-500"
                  />
                  {svc.availability}
                </p>

                <Link to={svc.path} className={cn(primaryLink, 'self-start')}>
                  Book Now
                  <LuArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="overflow-hidden rounded-3xl border border-base-200 bg-white">
          {secondary.map((svc) => (
            <Link
              key={svc.n}
              to={svc.path}
              className="group grid grid-cols-[2.5rem_1fr_1.5rem] items-start gap-x-5 border-b border-base-200 px-6 py-6 transition-colors last:border-b-0 hover:bg-green-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-700"
            >
              <span className="select-none pt-1 font-display text-sm text-gold-500">{svc.n}</span>
              <div>
                <h3 className="mb-1.5 font-display text-xl leading-tight text-base-900 transition-colors group-hover:text-green-800">
                  {svc.title}
                </h3>
                <p className="max-w-2xl text-sm leading-6 text-base-600">{svc.desc}</p>
              </div>
              <LuArrowRight
                aria-hidden="true"
                className="mt-1.5 h-5 w-5 shrink-0 text-base-400 transition-all duration-200 group-hover:translate-x-1 group-hover:text-green-700"
              />
            </Link>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
)
